import { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import VitrineNav from './VitrineNav';
/* ⛔ LA SOURCE UNIQUE DES TRANCHES D'EFFECTIF. Les cartes d'offres ET le menu
 *    « Nombre d'agents approximatif » en sont générés : le menu portait
 *    1-30 / 31-100 / 101-200 / 200+ quand les cartes portaient les vraies
 *    bornes. Deux listes écrites à la main divergent toujours. */
import { OFFRES, libelleTranche, offrePourTranche } from './offres';
import './LandingPage.css';

const OBJETS = ['Demande de démonstration', 'Demande de devis', 'Autre'];

/* ⓘ Les trois ancres que cette page sait recevoir depuis une autre page. Elles
 *   existent toutes comme `id` dans le rendu ci-dessous, et le CSS porte déjà
 *   un `scroll-margin-top: 80px` pour chacune. */
const ANCRES = ['demo', 'tarifs', 'fonctionnalites'];

/* ═══════════════════════════════════════════════════════════════════════════
 * LES HUIT CARTES DE « UNE PLATEFORME COMPLÈTE »
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ UNE CARTE = UNE FAMILLE, PAS UN DOMAINE. Le produit porte 433 routes sur
 *    53 préfixes et 95 écrans, soit 13 familles fonctionnelles. Leur donner une
 *    carte chacune transformerait la grille en fiche technique : elle cesserait
 *    de vendre. La règle posée le 11/09/2026 est donc : **chaque domaine réel
 *    doit être REPRÉSENTÉ, aucun n'a droit à sa carte propre**, et le plafond
 *    est de HUIT.
 *
 * ⚠️ LE CRITÈRE D'EXISTENCE EST L'ÉCRAN, PAS LE COMPTAGE EN BASE
 *    *(arbitrage de Dom, 11/09/2026)*. Une fonctionnalité existe si son écran
 *    existe et fonctionne. **Zéro ligne mesure l'USAGE, pas le produit** : la
 *    base ne porte que trois tenants, dont un de démonstration et une société
 *    qui ne fait pas de télésurveillance. Compter les lignes pour décider ce
 *    qu'on affiche reviendrait à laisser deux clients définir le catalogue.
 *
 * ⚠️ CE QUI RESTE DEHORS, ET POURQUOI : Validations (route de dépôt non câblée
 *    — file 15c), Assistanat, Import de données. Trois familles dont l'écran
 *    existe mais que Dom a explicitement exclues de la vitrine.
 *
 * ⓘ La 8ᵉ carte n'est pas un remplissage : le nombre suit les familles, et la
 *   grille suit le nombre (4×2 en large — voir `.vt-pillars`), jamais l'inverse.
 * ═══════════════════════════════════════════════════════════════════════════ */
const PILLARS = [
  {
    // Absorbe l'ancienne carte « Multi-pays » : convention collective, crans et
    // paramètres fiscaux sont le MÊME sujet que la paie — ce qui est légal par pays.
    //
    // ⚠️ « HEURES SUPPLÉMENTAIRES » EST REVENU LE 09/10/2026 — lot V3-bis,
    //    décision de Dom. Il avait été retiré le 11/09 avec deux autres termes ;
    //    la règle actée depuis renverse ce retrait : **la vitrine nomme les
    //    capacités du projet GSS-360, y compris celles en construction ; on ne
    //    retire d'une page que ce qui est FAUX, jamais ce qui n'est pas fini.**
    //    ✅ ET L'ASYMÉTRIE EST FERMÉE. Le hero porte « heures sup » depuis le lot
    //       V1 ; cette carte ne le portait pas. Les deux endroits disaient deux
    //       choses du même sujet — exactement ce que le commit `439785d` avait
    //       fermé, dans l'autre sens. **Un seul sujet, une seule vérité.**
    //    ⚠️ LE MOTEUR, LUI, N'A PAS CHANGÉ — mesuré le 09/10/2026, non corrigé
    //       par ce lot, à relire avant de rediscuter la ligne :
    //       `chargeurPaie.js:739` rend `majorations: []` en dur, zéro table de
    //       majorations au référentiel ; `calculPur.js:1318` REFUSE tout bulletin
    //       portant des heures non nulles plutôt que de les payer à zéro ;
    //       `paieController.js:393` rend un 422 sur `nb_heures_sup > 0`. Le
    //       calcul EXISTE (`calculPur.js:637`, `calculerMajorations()`) : ce qui
    //       manque est la table du référentiel et le chemin de saisie.
    //       ⓘ Les lignes citées par l'ancien commentaire (`:379` et `:938`)
    //         étaient PÉRIMÉES — le code avait bougé sous elles.
    //    ⓘ LES DEUX AUTRES TERMES RETIRÉS LE 11/09 RESTENT DEHORS, et leur motif
    //      n'est pas le même qu'« heures sup » :
    //    ② « Bordereau CNSS » — la route `/charges-sociales/bordereau` existe et
    //       calcule un bordereau complet, mais AUCUN ÉCRAN NE L'APPELLE
    //       (`exportPdf.js:25` renvoie les bordereaux de cotisation à la
    //       strate 2). Et `services/cnss/exportDrc.js` n'a aucun appelant et
    //       n'est pas déployé. Le critère est l'écran : il n'y en a pas.
    //       ⚠️ ET « CNSS » EST UN NOM PROPRE TOGOLAIS. Le produit le sait :
    //          `referentiel_pays.libelle_identifiant_social` rend CNSS pour TG,
    //          **CNPS pour CM**. L'écrire ici figerait sur une page qui vend
    //          19 pays le nom de l'organisme d'un seul — et serait FAUX pour le
    //          second tenant déjà en base (VIGIE CENTRALE, Cameroun).
    //    ③ « primes » — aucun écran ne gère les rubriques ; une prime est
    //       aujourd'hui un montant FIXE mensuel identique pour tous les agents.
    title: 'Paie, charges sociales & multi-pays',
    desc: 'Bulletins calculés, retenues et charges sociales, heures supplémentaires. Crans et planchers conventionnels, paramètres légaux par pays.',
    icon: 'M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8',
    main: true,
  },
  {
    // ⚠️ EN 2ᵉ POSITION, PAS EN DERNIÈRE (ordre voulu par Dom, 11/09/2026) :
    //    rangée 1 = la carte phare (2 cellules) + celle-ci.
    // Texte fourni mot pour mot par Dom — ne pas reformuler.
    title: 'Intelligence IA intégrée',
    desc: 'Votre conformité sous surveillance continue : écarts de paie, anomalies comptables, contrats signés à perte. Chaque alerte cite son texte et sa date d\'effet — l\'IA explique, le moteur calcule.',
    icon: 'M12 2a7 7 0 017 7c0 2.4-1.2 4.5-3 5.7V17H8v-2.3C6.2 13.5 5 11.4 5 9a7 7 0 017-7zM8 21h8M9 17v4M15 17v4',
  },
  {
    title: 'Ressources humaines',
    desc: 'Agents, contrats, congés, pointages, formations et attestations, fiches de poste et états du personnel.',
    icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 7m-4 0a4 4 0 108 0 4 4 0 00-8 0M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75',
  },
  {
    // Absorbe planning/vacations (2 463 lignes en prod, la 2ᵉ table du produit)
    // et les audits de sécurité : ce sont des activités de TERRAIN.
    title: 'Commandement & opérations terrain',
    desc: 'Supervision temps réel, sites et postes, planning des vacations, rondes, incidents, audits de sécurité et plans de prévention.',
    icon: 'M2 3h20v14H2zM8 21h8M12 17v4',
  },
  {
    // ⚠️ AUCUNE MENTION DE « FACTURE PDF » : `factureRoutes.js` n'expose pas de
    //    route PDF pour les factures CLIENT (les devis et les factures SaaS, si).
    //    Vérifié le 11/09/2026 — on ne promet pas un export qui n'existe pas.
    title: 'Clients, devis & facturation',
    desc: 'Fiches clients, offres et tarifs, devis chiffrés, contrats de prestation, facturation et encaissements.',
    icon: 'M6 2h12a1 1 0 011 1v18l-3-2-3 2-3-2-3 2V3a1 1 0 011-1zM9 7h6M9 11h6M9 15h4',
  },
  {
    title: 'Équipements, flotte & cynophile',
    desc: 'Matériel et dotations aux agents, véhicules et entretien, unités cynophiles et suivi sanitaire.',
    icon: 'M1 3h15v13H1zM16 8h4l3 3v5h-7zM5.5 19a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM18.5 19a2.5 2.5 0 100-5 2.5 2.5 0 000 5z',
  },
  {
    // Absorbe les 20+ routes d'export PDF/Excel, transversales à 12 domaines :
    // elles n'avaient aucune carte alors qu'elles traversent tout le produit.
    title: 'Comptabilité, reporting & exports',
    desc: 'Plan comptable, écritures, grand livre, balance, export FEC. Bulletins, livre de paie et états en PDF et Excel.',
    icon: 'M4 2v20h16V2H4zm4 4h8M8 10h8M8 14h5',
  },
  {
    title: 'Télésurveillance',
    desc: 'Alarmes, vidéo, journal de veille, interventions.',
    icon: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
  },
];

export default function LandingPage() {
  const [form, setForm] = useState({ objet: OBJETS[0], offre: '', nom: '', entreprise: '', nombre_agents: '', email: '', telephone: '', message: '' });
  /* ⛔ L'OFFRE NE SE CHOISIT PLUS : ELLE SE DÉDUIT DE LA TRANCHE. Deux menus
   *    indépendants laissaient demander ELITE+ pour dix agents, et le serveur
   *    l'acceptait — il validait l'offre contre une liste et ignorait
   *    l'effectif. *La contradiction n'est plus interdite par un contrôle :
   *    elle est devenue impossible.*
   * ⓘ Recalculée à chaque rendu, jamais stockée dans l'état : une valeur
   *   dérivée que l'on garde se désynchronise le jour où l'on oublie de la
   *   mettre à jour. */
  const offreDeduite = offrePourTranche(form.nombre_agents);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  /* ⓘ `menuOpen` a été retiré d'ici : l'état d'ouverture du menu mobile vit
   *   désormais dans `VitrineNav`, avec le bouton qui le change et le panneau
   *   qu'il affiche. Un état qui ne sert plus qu'à être déclaré fait échouer le
   *   build (`CI=true` traite `no-unused-vars` en erreur) — et c'est bien ainsi. */

  const location = useLocation();

  /* ⓘ Un seul endroit décide COMMENT on défile : doux par défaut, instantané si
   *   la personne a demandé moins de mouvement. `html { scroll-behavior: smooth }`
   *   ne suffit pas — `scrollIntoView` doit aussi cesser de forcer `smooth`. */
  const defiler = useCallback((id) => {
    const cible = document.getElementById(id);
    if (!cible) return;
    const sobre = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    cible.scrollIntoView({ behavior: sobre ? 'auto' : 'smooth', block: 'start' });
  }, []);

  const scrollToDemo = useCallback((objet, offre = '') => {
    setForm((f) => ({ ...f, objet, offre }));
    setTimeout(() => defiler('demo'), 50);
  }, [defiler]);

  /* ═══════════════════════════════════════════════════════════════════════════
   * L'ANCRE D'ARRIVÉE — CE QUI MANQUAIT, ET POURQUOI ÇA NE SE VOYAIT PAS
   * ═══════════════════════════════════════════════════════════════════════════
   * ⚠️ IL N'Y AVAIT AUCUNE GESTION DE HASH DANS TOUTE LA VITRINE. Mesuré :
   *    `useLocation`, `location.hash`, `ScrollRestoration`, `hashchange` →
   *    ZÉRO occurrence dans `app/public/`. Le seul défilement du site était le
   *    `onClick` de cette page. Conséquence : « Démo », « Tarifs » et
   *    « Fonctionnalités » depuis la FAQ, « Qui sommes-nous », les mentions
   *    légales ou la confidentialité déposaient le visiteur **en haut de
   *    l'accueil**, à lui de chercher la section.
   * ⚠️ ET LE CSS LE CROYAIT RÉGLÉ : les trois règles `scroll-margin-top: 80px`
   *    (`#demo`, `#tarifs`, `#fonctionnalites`) étaient du CODE MORT — elles ne
   *    s'appliquent qu'à un défilement VERS l'ancre, qui n'avait jamais lieu.
   *    Elles redeviennent vivantes avec cet effet.
   * ⚠️ LA CAUSE N'ÉTAIT PAS L'ANCRE, C'ÉTAIT LE `<a href>`. Les pages juridiques
   *    pointaient `/vitrine#demo` en lien plein : un rechargement complet de
   *    l'application. Au rechargement, le navigateur traite le hash AVANT que
   *    React ne monte — la section n'existe pas encore, donc aucun défilement.
   *    C'est l'échec classique du hash en SPA. Les liens sont passés en `<Link>`.
   * ⓘ DÉPEND DE `location.key` AUTANT QUE DU HASH : deux clics de suite sur le
   *   même lien ne changent pas le hash mais produisent deux navigations. Sans
   *   la clé, le second clic ne défilerait pas.
   * ⓘ L'OBJET EST POSÉ EXPLICITEMENT pour `#demo`. L'état initial le porte déjà,
   *   mais s'appuyer sur ce défaut serait dépendre de l'ordre des champs d'un
   *   `useState` — et surtout : après un clic sur une carte de tarif l'objet vaut
   *   « Demande de devis », donc un retour par `/#demo` garderait le mauvais
   *   objet sans cette ligne.
   * ═══════════════════════════════════════════════════════════════════════════ */
  useEffect(() => {
    const id = location.hash.replace('#', '');
    if (!ANCRES.includes(id)) return;
    if (id === 'demo') setForm((f) => ({ ...f, objet: OBJETS[0] }));
    /* Deux trames : la première laisse React poser le DOM, la seconde laisse le
     * navigateur calculer la mise en page. Sans cela on défile vers une position
     * mesurée avant que les sections aient leur hauteur. */
    requestAnimationFrame(() => requestAnimationFrame(() => defiler(id)));
  }, [location.key, location.hash, defiler]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setError('');
    try {
      const res = await fetch('/api/public/demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, offre: offreDeduite ? offreDeduite.code : '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur serveur');
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally { setSending(false); }
  };

  const updateField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  return (
    <div className="vt">
      <VitrineNav />

      {/* Hero */}
      <section className="vt-hero">
        {/* ⓘ « IA-NATIVE » s'écrit désormais en capitales DANS LA SOURCE, et cela
          *   ne change RIEN à l'écran : `.vt-hero-badge` porte
          *   `text-transform: uppercase` depuis toujours, donc « IA-native »
          *   s'affichait déjà « IA-NATIVE ». La source dit maintenant ce que le
          *   visiteur lit — un `grep` sur la vitrine ne mentira plus. */}
        <div className="vt-hero-badge">
          <span className="vt-hero-badge-dot" />
          IA-NATIVE
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            TITRE — TEXTE DE DOM, APPLIQUÉ À L'IDENTIQUE (lot V3-ter, 09/10/2026)
            ═══════════════════════════════════════════════════════════════════
            ⚠️ LE SEGMENT ROUGE EST INSÉCABLE, ET C'EST CE QUI DÉCIDE S'IL TIENT.
               `.vt-hero-title em` porte `white-space: nowrap` : « une seule
               plateforme » ne peut PAS se couper. Vérifié avant d'écrire :
                 · segment ACTUEL  « gestion intelligente » = 20 caractères
                 · segment NOUVEAU « une seule plateforme » = 20 caractères
               **Longueur identique, même police, même corps → même largeur
               rendue.** L'actuel tient en production à 375 px ; le nouveau tient
               donc à l'identique. À 375 px la largeur utile est de 343 px
               (375 − 2 × 16 de padding) et le titre est à 24 px.
            ⓘ ET LE TITRE ENTIER RACCOURCIT : 103 → 82 caractères. Il se replie
              sur MOINS de lignes, pas plus — le risque de hauteur baisse aussi.
            ⓘ C'est un raisonnement arithmétique sur la contrainte réelle, pas une
              mesure rendue : il n'y a pas de navigateur ici. La vérification
              visuelle à 375 px reste à faire à l'œil.
            ═══════════════════════════════════════════════════════════════════ */}
        <h1 className="vt-hero-title">
          Toute votre société de sécurité privée, pilotée depuis <em>une seule plateforme</em>.
        </h1>

        {/* ⓘ `.vt-hero-sub` N'EST PAS UNE CLASSE NOUVELLE — elle existait déjà
          *   dans `LandingPage.css` (17 px, 580 px de mesure, et ses deux
          *   tailles aux points de rupture : 15 px à 768, 14 px à 480) et
          *   n'était utilisée PAR AUCUN élément. C'était une règle orpheline ;
          *   le sous-titre la remet au travail. **Aucune ligne de CSS n'est
          *   ajoutée par ce lot.** */}
        <p className="vt-hero-sub">
          Pointage, paie, rondes, contrats et comptabilité SYSCOHADA réunis dans un
          seul système, conçu pour les sociétés de sécurité privée et les groupes
          multi-pays de la zone OHADA. Vous savez à tout moment qui est à son poste,
          ce que coûte chaque agent et ce que rapporte chaque contrat.
        </p>

        {/* ═══════════════════════════════════════════════════════════════════
            LE BOUTON EST REMONTÉ AVANT LES QUATRE LIGNES, ET C'EST UN CHANGEMENT
            D'ORDRE, PAS DE MARGES
            ═══════════════════════════════════════════════════════════════════
            ⚠️ POURQUOI : le bouton n'était PAS visible sans défilement sur le
               poste de Dom à 100 %. Constat réel, et il prime sur mes calculs —
               qui reposaient sur deux valeurs que je ne pouvais pas mesurer ici
               (l'avance moyenne d'un glyphe, puisqu'aucune police n'est chargée,
               et la hauteur des barres du navigateur). Une pile de hauteur
               estimée ne décide pas de ce qu'un écran montre.
            ➡️ LA CORRECTION NE JOUE PLUS SUR LES MARGES. Resserrer encore
               aurait été poursuivre la même erreur : un rognage calculé contre
               une hauteur inconnue. En remontant l'appel à l'action AVANT le
               bloc le plus haut de la page — les quatre lignes, deux à trois
               lignes chacune — il devient le quatrième élément du hero et non
               le sixième. Sa position ne dépend plus de la hauteur des lignes.
            ⓘ ORDRE : pastille · titre · sous-titre · BOUTON · preuve · les
              quatre lignes. Textes, gras, couleurs, tailles et centrage
              inchangés.
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="vt-hero-actions">
          <button type="button" className="vt-btn-primary" onClick={() => scrollToDemo(OBJETS[0])}>
            Demander une démo
          </button>
        </div>

        {/* ⓘ LE ✦ EST PARTI. Ce glyphe (U+2726) n'existe dans aucune des polices
          *   de repli de façon fiable, et le texte fourni n'en porte pas.
          * ⓘ `.vt-hero-proof-accent` RESTE EMPLOYÉ, sur « déjà en service au
          *   Togo » : c'est le traitement que cette ligne a toujours eu, et ne
          *   plus l'utiliser laisserait une règle CSS morte derrière nous. */}
        <div className="vt-hero-proof">
          Conçu par des professionnels de la sécurité privée,{' '}
          <span className="vt-hero-proof-accent">déjà en service au Togo</span>.
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            LA LIGNE DE PRIX — MÊME STYLE QUE LA PREUVE, COULEUR ATTÉNUÉE
            ═══════════════════════════════════════════════════════════════════
            ⓘ ELLE RÉEMPLOIE `.vt-hero-proof` EN ENTIER et n'ajoute qu'une
              couleur et une marge : pas un style neuf, une variante. Le
              modificateur ne porte que ce qui diffère.
            ⚠️ ELLE DOIT RESTER D'ACCORD AVEC LA SECTION TARIFS, mot pour mot sur
               le fond — c'est la même promesse dite deux fois à deux endroits :
               · « par agent actif »  ← les cartes disent « Vous ne payez que vos
                 agents actifs » et « X FCFA par agent et par mois » ;
               · « sans engagement »  ← la note de bas de section dit « Sans
                 engagement — facturation mensuelle ou annuelle » ;
               · « tarifs nets, sans TVA » ← le sous-titre des offres dit
                 « Tarifs nets : GSS-360 est facturé sans TVA. »
               ➡️ Si l'une des trois change dans la section tarifs, elle change
                  ICI aussi. Deux endroits, une seule vérité.
            ═══════════════════════════════════════════════════════════════════ */}
        <div className="vt-hero-proof vt-hero-proof--prix">
          Tarif par agent actif · sans engagement · tarifs nets, sans TVA
        </div>

        {/* ⓘ L'INTITULÉ RÉEMPLOIE `.vt-preview-label` — la classe des petits
          *   intitulés en capitales déjà présents sur la page (celui de la
          *   capture : « Centre de commandement — supervision en temps réel »).
          *   Elle porte déjà `text-transform: uppercase`, `letter-spacing` et la
          *   couleur ; `.vt-hero-eyebrow` ne règle QUE les marges.
          * ⚠️ ET C'EST LUI QUI PORTE MAINTENANT LES 28 px au-dessus du bloc.
          *    `.vt-hero-lines` perd sa marge haute : sinon l'intitulé flotterait
          *    à 38 px de la liste qu'il annonce, puisque les marges d'éléments
          *    flex ne fusionnent pas. */}
        <p className="vt-preview-label vt-hero-eyebrow">UNE VUE À 360° DE VOTRE SOCIÉTÉ</p>

        <ul className="vt-hero-lines">
          {/* ═══════════════════════════════════════════════════════════════════
              LES QUATRE LIGNES — TEXTES DE DOM, APPLIQUÉS À L'IDENTIQUE (V3-ter)
              ═══════════════════════════════════════════════════════════════════
              Elles remplacent les quatre lignes d'inventaire qui disaient « Paie
              calculée et conforme », « RH automatisées », « Opérations terrain »,
              « Comptabilité OHADA/SYSCOHADA ». Aucun chiffre, aucun nom de
              client, aucun marqueur daté. Le patron « Titre — détail » est
              conservé, et la mise en page ne change pas d'un pixel.

              ⚠️ « heures sup » EST TOUJOURS NOMMÉ — deuxième ligne, « heures sup
                 et primes comprises ». Ce n'est pas un détail de rédaction :
                 c'est la règle actée le 09/10/2026, qui renverse le retrait du
                 11/09 (commit `439785d`) — **la vitrine nomme les capacités du
                 projet GSS-360, y compris celles en construction ; on ne retire
                 d'une page que ce qui est FAUX, jamais ce qui n'est pas fini.**
              ✅ ET L'ASYMÉTRIE RESTE FERMÉE. Le hero le nomme ici, et la carte
                 « Paie, charges sociales & multi-pays » le nomme aussi depuis le
                 lot V3-bis. Les deux endroits disent la même chose — c'est ce que
                 `439785d` avait fermé dans l'autre sens, et il faut le garder
                 fermé : **si l'un des deux change, l'autre change avec.**
              ⚠️ LE MOTEUR, LUI, N'A PAS CHANGÉ — mesuré le 09/10/2026, non
                 corrigé par ce lot, à relire avant de rediscuter la ligne :
                 `chargeurPaie.js:739` rend `majorations: []` en dur, zéro table de
                 majorations au référentiel ; `calculPur.js:1318` REFUSE tout
                 bulletin portant des heures non nulles plutôt que de les payer à
                 zéro ; `paieController.js:393` rend un 422 sur `nb_heures_sup > 0`.
                 Le calcul EXISTE (`calculPur.js:637`, `calculerMajorations()`) :
                 ce qui manque est la table du référentiel et le chemin de saisie.
                 ⓘ Un commentaire qui cite une ligne cite une cible mouvante : les
                   deux lignes citées ici avant le 09/10 (`:379` et `:938`)
                   étaient PÉRIMÉES, le code avait bougé sous elles.
              ⓘ LA TROISIÈME LIGNE EST LA SEULE À PORTER LE MULTI-TENANT, et elle
                le dit par le bon bout : « chaque société pose ses règles sur la
                base légale de son pays ». Le pays fixe le droit, la société fixe
                ses règles. Et « Le système alerte, vous décidez » dit la limite
                que le produit tient réellement — l'IA signale, elle n'arbitre pas.
              ═══════════════════════════════════════════════════════════════════ */}
          {/* ⚠️ LE TIRET DE TÊTE EST POSÉ PAR LE CSS, PAS PAR LE TEXTE.
              `.vt-hero-lines li::before { content: '—' }` en rouge #D13B3B. Les
              quatre libellés ne commencent donc PAS par un tiret — l'écrire ici
              en afficherait deux. Le second tiret, celui qui sépare le titre du
              détail, est bien dans le texte : c'est le patron « Titre — détail »
              des lignes d'origine, conservé. */}
          {/* ⓘ LE `<strong>` PORTE LE TITRE DE LA LIGNE, PAS UNE EMPHASE. Il
            *   marque la partie avant le tiret — ce que la ligne annonce — et le
            *   détail suit en poids normal. `.vt-hero-lines li strong` fixe le
            *   poids à 600 et `color: inherit` garantit qu'AUCUNE couleur ne
            *   change : le gras ne doit pas devenir un second accent rouge à
            *   côté du tiret, qui est déjà en #D13B3B. */}
          <li><strong>Administratif</strong> — paie et RH conformes aux règles de votre pays et de votre société.</li>
          <li><strong>Opérations</strong> — gardiennage et sécurité électronique sur une seule carte : postes, rondes, incidents, alarmes et vidéo en temps réel.</li>
          {/* ⚠️ CETTE LIGNE A ÉTÉ RÉÉCRITE POUR SORTIR D'UNE REDITE. Sa version
              précédente disait « offres chiffrées au coût chargé des postes,
              marge mesurée site par site » — et « au coût chargé des postes »
              comme « site par site » se retrouvaient MOT POUR MOT dans la
              réponse 4 de « Vos contraintes, nos réponses », quelques centaines
              de pixels plus bas. Deux occurrences chacun, mesurées sur le
              bundle servi.
              ⓘ LA CORRECTION EST CÔTÉ HERO SEULEMENT (décision de Dom) : la
                réponse 4 garde le détail — le coût chargé, le site par site —
                et le hero garde l'intention. Même partage que celui fait entre
                le hero et la carte IA : le haut de page annonce, le bas
                détaille, et aucun des deux ne récite l'autre.
              ⓘ Troisième fois que ce motif se présente sur cette page (carte
                IA, puis réponse 3, puis celle-ci). Le réflexe à garder : après
                tout ajout de texte au hero, relire ce qui est déjà plus bas. */}
          <li><strong>Rentabilité</strong> — vos offres analysées, vos contrats mesurés, votre rentabilité évaluée.</li>
          {/* ⚠️ CETTE LIGNE A ÉTÉ RÉÉCRITE POUR SORTIR D'UNE REDITE (lot V3-ter,
              seconde passe, nuit du 09 au 10/10/2026).
              Sa première version reprenait la carte « Intelligence IA intégrée »
              presque mot pour mot : « écarts de paie », « anomalies
              comptables », « contrats signés à perte », « le moteur calcule »
              apparaissaient DEUX fois sur la page, à environ 600 px d'intervalle.
              C'était le défaut même que le lot V3 avait pour objectif de
              supprimer, revenu par un autre chemin.
              ⚠️ LA CORRECTION EST CÔTÉ HERO SEULEMENT (décision de Dom) : la
                 carte IA n'est PAS touchée. Le hero dit maintenant ce que l'IA
                 FAIT POUR LE DIRIGEANT — elle surveille, elle alerte avant que
                 ça coûte — et la carte garde le détail de ce qu'elle regarde.
                 Deux niveaux de lecture, plus deux fois le même inventaire.
              ⓘ « avant que l'erreur ne coûte » est la seule promesse
                d'ANTÉRIORITÉ de la page. Elle est tenable : les alertes portent
                sur des écarts constatés AVANT émission (contrôle pré-paie,
                audit avant export), pas sur des pièces déjà sorties. */}
          <li><strong>Conformité</strong> — une IA qui signale l&apos;écart avant qu&apos;il ne coûte.</li>
        </ul>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════
          VOS CONTRAINTES, NOS RÉPONSES
          ═══════════════════════════════════════════════════════════════════════
          ⓘ À QUI ELLE PARLE, ET POURQUOI ELLE EST ICI. Le hero parle au
            DIRIGEANT et aux groupes multi-pays — contrôle, coûts, marge, risque.
            Cette section parle à son ÉQUIPE : le comptable, la RH, les
            opérations. Elle est donc placée juste après le hero, avant
            l'inventaire des familles : on répond d'abord à ce qui bloque
            aujourd'hui, on détaille le produit ensuite.

          ⚠️ AUCUN COMPOSANT VISUEL N'EST INVENTÉ. La section réemploie `.vt-fonc`
             (structure, padding, et son titre `.vt-fonc-title`) et les cartes
             réemploient `.vt-pillars` / `.vt-pillar` / `.vt-pillar-icon` /
             `.vt-pillar-title` / `.vt-pillar-desc` — exactement les classes de
             « Une plateforme complète ». Le responsive suit donc le même :
             3 colonnes, 2 à 768 px, **1 seule à 480 px**, ce qui donne une paire
             par ligne en mobile 375 px.
          ⓘ UNE SEULE DÉCLARATION EST AJOUTÉE : `.vt-contraintes { background }`.
            Sans elle, cette section (#1E1E26 héritée de `.vt-fonc`) toucherait
            « Une plateforme complète », qui porte le même fond — la frontière
            entre les deux disparaîtrait. En #16161C elle prolonge le hero et
            laisse le #1E1E26 marquer le début de l'inventaire.

          ⚠️ LA CONTRAINTE EST DANS LE TITRE DE CARTE, LA RÉPONSE DANS SON TEXTE,
             et la flèche de l'icône porte le « → ». C'est le seul endroit du
             produit où un titre de carte nomme un PROBLÈME et non une
             capacité — à savoir si on relit cette section plus tard.
          ═══════════════════════════════════════════════════════════════════════ */}
      <section className="vt-fonc vt-contraintes">
        <h2 className="vt-fonc-title">Vos contraintes, nos réponses</h2>
        <div className="vt-pillars">
          {[
            { contrainte: 'Pointages sur Excel',
              reponse: 'Le pointage, journalier ou mensuel, alimente directement le moteur de paie.' },
            { contrainte: 'Paie refaite à la main',
              reponse: 'Cotisations, impôt, heures sup et primes sont calculés selon le référentiel daté de votre pays et vos propres règles.' },
            { contrainte: 'Rondes invérifiables',
              reponse: 'Postes, rondes, incidents et alarmes sont tracés en temps réel sur le centre de commandement.' },
            { contrainte: 'Contrats dont on ignore la marge',
              reponse: 'Chaque offre est chiffrée au coût chargé des postes et chaque contrat est mesuré site par site.' },
            { contrainte: 'Outils séparés',
              reponse: 'Une donnée saisie une fois sert partout : paie, facturation et comptabilité SYSCOHADA reposent sur les mêmes données, avec des sociétés strictement isolées.' },
          ].map((p) => (
            <div key={p.contrainte} className="vt-pillar">
              {/* ⓘ La flèche occupe le créneau d'icône que `.vt-pillar` porte
                * déjà, avec le même `viewBox` et la même épaisseur de trait que
                * les icônes des autres cartes. C'est elle qui dit « donc ». */}
              <svg className="vt-pillar-icon" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                aria-hidden="true">
                <line x1="4" y1="12" x2="18" y2="12" /><polyline points="13,7 18,12 13,17" />
              </svg>
              <div className="vt-pillar-title">{p.contrainte}</div>
              <div className="vt-pillar-desc">{p.reponse}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Fonctionnalités */}
      <section id="fonctionnalites" className="vt-fonc">
        <h2 className="vt-fonc-title">Une plateforme complète</h2>
        <p className="vt-fonc-sub">Tout ce dont une société de sécurité a besoin, réuni dans un seul outil.</p>
        <div className="vt-pillars">
          {PILLARS.map((p, i) => (
            <div key={i} className={`vt-pillar${p.main ? ' vt-pillar--main' : ''}`}>
              {/* ⚠️ BADGE « ★ Fonctionnalité phare » RETIRÉ LE 11/09/2026.
                  La carte occupe DEUX cellules et porte un liseré rouge avec son
                  dégradé : la hiérarchie est déjà dite par la forme. Un badge
                  qui répète ce que la mise en page montre déjà n'ajoute rien.
                  ⓘ `main: true` RESTE — il porte `.vt-pillar--main`, donc le
                    `grid-column: span 2` et le liseré. Ne pas le retirer en
                    croyant supprimer le badge. */}
              <svg className="vt-pillar-icon" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d={p.icon} />
              </svg>
              <div className="vt-pillar-title">{p.title}</div>
              <div className="vt-pillar-desc">{p.desc}</div>
            </div>
          ))}
        </div>
        <p className="vt-fonc-note">
          Fonctionnalités modulables — activez uniquement ce dont votre société a besoin.
        </p>
      </section>

      {/* Aperçu produit */}
      <section className="vt-preview">
        <p className="vt-preview-label">Centre de commandement — supervision en temps réel</p>
        <div className="vt-preview-frame">
          <div className="vt-preview-bar">
            <div className="vt-preview-dots">
              <span /><span /><span />
            </div>
            <div className="vt-preview-url">gss360.net/commandement</div>
          </div>
          <img
            src="/images/preview-commandement.webp"
            alt="Centre de commandement GSS-360 — carte opérationnelle, alertes en temps réel"
            width="1919" height="778" loading="lazy" decoding="async"
          />
          {/* VIDEO VERSION — activer quand preview-commandement.mp4 disponible
          <video
            src="/images/preview-commandement.mp4"
            autoPlay muted loop playsInline
            className="vt-preview-video"
            poster="/images/preview-commandement.webp"
          />
          */}
        </div>
      </section>

      {/* Tarifs */}
      <section id="tarifs" className="vt-tarifs">
        <h2 className="vt-tarifs-title">Nos offres</h2>
        {/* 🔴 CETTE LIGNE ANNONÇAIT UNE TAXE QUI N'ARRIVE JAMAIS — corrigée le
            14/09/2026. Elle disait : « Montants hors taxes — la TVA s'ajoute au
            taux en vigueur dans votre pays. » Le commentaire qui l'accompagnait
            invoquait une règle du 02/09 et concluait qu'une page taisant le « HT »
            annonce un prix que le client ne paiera pas.
            ⚠️ LE RAISONNEMENT ÉTAIT JUSTE, SA PRÉMISSE NE L'EST PLUS. Décision de
               Dom du 13/09/2026, actée : **la TVA est un attribut de celui qui
               ÉMET**. Gladiator Security Systems, éditeur, n'est PAS assujettie —
               aucune facture d'abonnement ne porte de TVA, pour aucun client.
               La base le refuse (`chk_facture_saas_sans_tva`), le moteur le refuse
               (`TVA_INTERDITE_SUR_FACTURE_SAAS`), et le PDF imprime « TVA non
               applicable - emetteur non assujetti ».
            ➡️ La page annonçait donc un supplément que la facture ne portera
               jamais : le défaut exact qu'elle prétendait éviter, dans l'autre sens.
            ⚠️ ET ELLE DIT D'ABORD CE QUI EST, PAS CE QUI MANQUE (règle 25) : le
               montant affiché EST le montant facturé. La raison vient ensuite.
            ⓘ La TVA que le TENANT applique à SES clients est un autre sujet, et
              elle reste entière — FOX y est assujettie, Gladiator non.
            ⚠️ PHRASE REMPLACÉE LE 09/10/2026, TEXTE DE DOM APPLIQUÉ À L'IDENTIQUE.
               Elle disait : « Le montant affiché est le montant facturé — l'éditeur
               n'est pas assujetti à la TVA. » Elle n'était pas FAUSSE sur l'effet
               (aucune facture d'abonnement ne porte de TVA) mais elle énonçait un
               RÉGIME FISCAL, et pas le bon : la règle actée de l'éditeur est
               **le réel SANS TVA**, ce qui n'est pas « non assujetti ». La nouvelle
               phrase dit le PRIX et s'arrête là — une page de tarifs n'a pas à
               qualifier le régime de celui qui émet.
            ⚠️ PÉRIMÈTRE DU REMPLACEMENT, BORNÉ PAR DOM : cette section de TARIFS
               UNIQUEMENT. Aucune mention de TVA relative à la facturation d'un
               TENANT à SES clients n'est touchée — c'est une capacité du produit et
               le régime propre de chaque société. Ne pas « harmoniser » plus tard
               `InvoicesPage`, `QuotesPage`, `EquipmentPage` ni
               `SuperAdminReferentielPays` sur cette phrase : ils parlent d'un
               AUTRE émetteur. */}
        <p className="vt-tarifs-sub">
          Toutes les fonctionnalités incluses. Vous choisissez votre volume.<br />
          <strong>Tarifs nets : GSS-360 est facturé sans TVA.</strong>
        </p>

        <div className="vt-tarifs-grid">
          {/* ⚠️ ALIGNE SUR LA FORMULE DE FACTURATION (decision Dom, 03/09/2026).
              Avant, cette page vendait un FORFAIT FIXE par tranche (105 000 /
              300 000 / 500 000 F, « turnover inclus ») alors que le systeme
              facture A L'AGENT ACTIF : un client ALPHA a 10 agents lisait
              105 000 ici et aurait paye 40 000. Le site promettait un modele
              que le produit n'appliquait pas.
              Desormais : prix par agent + forfait MINIMUM, exactement
                total_ht = max(effectif_actif x prix_agent + modules, plancher). */}
          {/* ⚠️ TEXTES FOURNIS PAR DOM LE 14/09/2026, APPLIQUÉS À L'IDENTIQUE.
              Aucun n'est reformulé, aucun chiffre n'est recalculé ici.
              ⓘ VÉRIFIÉS contre le moteur avant écriture — ils tombent juste :
                15×2 000 = 30 000 relevé au plancher 40 000 · 25×2 000 = 50 000 ·
                40×1 500 = 60 000 · 100×1 500 = 150 000 · 150×1 200 = 180 000 ·
                200×1 200 = 240 000. Et les 240 000 d'ELITE+ sont EXACTEMENT le
                plancher posé en base par la migration 052 le matin même.
              ⚠️ « forfait minimum X FCFA / mois » A DISPARU DES QUATRE CARTES.
                Il disait la même chose que « À partir de », mais sous une forme
                que le lecteur devait convertir lui-même — et un lecteur qui
                convertit un tarif se trompe un jour.
              ⚠️ ELITE+ N'AFFICHE PLUS « Sur devis » NU : elle annonce son point
                d'entrée. La dernière frontière était la seule à ne pas dire son
                seuil, donc la seule à pouvoir surprendre. */}
          {[
            { ...OFFRES[0], nom: OFFRES[0].code, agents: libelleTranche(OFFRES[0]),
              exemples: '15 agents : 40 000 F/mois · 25 agents : 50 000 F/mois', badge: null },
            { ...OFFRES[1], nom: OFFRES[1].code, agents: libelleTranche(OFFRES[1]),
              exemples: '40 agents : 60 000 F/mois · 100 agents : 150 000 F/mois', badge: 'popular' },
            /* ⚠️ FORFAIT MINIMUM 130 000 → 150 000 LE 13/09/2026, ET LA BASE A ÉTÉ
               CORRIGÉE EN PREMIER (migration 046). Le défaut n'était pas ici : il
               était dans `public.grille_tarifaire`, la table que lit la FACTURATION.
               À 130 000, un client passant de 100 à 101 agents voyait sa facture
               BAISSER de 20 000 F — max(100×1500)=150 000 contre max(101×1200,130 000)
               =130 000. L'inversion courait de 101 à 124, égalité à 125.
               ⓘ RÈGLE À TENIR : le plancher d'une tranche doit être au moins égal au
                 plafond de la précédente (effectif_max × prix_agent).
               ❌ CE COMMENTAIRE AFFIRMAIT que « la migration 046 porte un garde qui
                 refuse toute grille où cette propriété est fausse ». C'ÉTAIT FAUX :
                 mesuré le 14/09/2026 en local ET en prod, aucune contrainte, aucun
                 déclencheur, aucune fonction ne portait cette règle — le garde de la
                 046 avait été éprouvé dans une transaction ANNULÉE puis jamais
                 committé (règle 58). ✅ C'est la **migration 052** qui la pose
                 réellement : `trg_grille_continuite`, différé au COMMIT, vérifié
                 présent en base après application. */
            { ...OFFRES[2], nom: OFFRES[2].code, agents: libelleTranche(OFFRES[2]),
              exemples: '150 agents : 180 000 F/mois · 200 agents : 240 000 F/mois', badge: 'portail' },
            /* ELITE+ : point d'entrée annoncé, prix négocié. Ni exemples ni
               mention de paiement annuel — le texte fourni n'en porte pas, et
               on n'ajoute rien à une carte dont le prix se discute. */
            { ...OFFRES[3], nom: OFFRES[3].code, agents: libelleTranche(OFFRES[3]),
              exemples: null, badge: 'devis' },
          ].map((pack) => (
            <div key={pack.nom} className={`vt-tarifs-card${pack.badge === 'popular' ? ' vt-tarifs-card--popular' : ''}`}>
              {pack.badge === 'popular' && <div className="vt-tarifs-badge vt-tarifs-badge--popular">Le plus populaire</div>}
              <div className="vt-tarifs-pack">{pack.nom}</div>
              <div className="vt-tarifs-agents">{pack.agents}</div>
              {/* ⚠️ L'ORDRE EST CELUI QUE DOM A FIXÉ, et il n'est pas indifférent :
                  le montant qu'on paie AU MINIMUM vient avant le prix unitaire,
                  parce que c'est lui qu'un dirigeant compare d'abord.
                  ⚠️ TROIS NIVEAUX (14/09/2026) — surtitre, montant seul, unité.
                     « À partir de » était INSÉRÉ DANS LA LIGNE du montant : le
                     grand nombre perdait sa force et la ligne devenait longue,
                     donc coupable de se couper. Il monte au-dessus ; le montant
                     retrouve une ligne à lui.
                  ⚠️ LE MONTANT NE SE COUPE JAMAIS : `white-space: nowrap` sur
                     `.vt-tarifs-montant`. Un « 240 » sur une ligne et « 000 »
                     sur la suivante ne serait pas une gêne de mise en page,
                     ce serait un PRIX FAUX. */}
              <div className="vt-tarifs-prix">
                <div className="vt-tarifs-surtitre">À partir de</div>
                <div className="vt-tarifs-montant">{pack.aPartirDe}</div>
                <div className="vt-tarifs-unite">FCFA / mois</div>
              </div>
              {pack.parAgent ? (
                <>
                  <div className="vt-tarifs-forfait">{pack.parAgent} FCFA par agent et par mois</div>
                  <div className="vt-tarifs-exemples">{pack.exemples}</div>
                  <div className="vt-tarifs-fixe">Vous ne payez que vos agents actifs</div>
                  {/* ⚠️ « 2 MOIS OFFERTS » A ÉTÉ RETIRÉ LE 14/09/2026 — C'ÉTAIT FAUX.
                      « Offerts » annonce que rien d'autre n'est dû. La règle est
                      autre : les dix mois couvrent l'effectif DE SOUSCRIPTION, et
                      tout dépassement produit un complément qui reste dû. Un client
                      qui embauche en cours d'année paie donc plus que dix mois.
                      ⓘ Ce badge était sur CHAQUE carte, dans la section Tarifs —
                        l'endroit le plus lu de la page. Il a survécu à la première
                        correction de ce lot parce que je n'avais regardé que la FAQ :
                        même erreur que le 13/09, où une vérification n'avait porté
                        que sur LandingPage et manquait FaqPage et QuiSommesNous.
                        ➡️ Une promesse tarifaire se cherche sur TOUTES les pages. */}
                  <div className="vt-tarifs-badge vt-tarifs-badge--annual">Paiement annuel : 10 mois réglés pour 12</div>
                  {pack.badge === 'portail' && <div className="vt-tarifs-badge vt-tarifs-badge--portail">Portail client inclus</div>}
                </>
              ) : (
                <div className="vt-tarifs-devis-text">Tarification personnalisée selon vos besoins</div>
              )}
              <button type="button" className="vt-tarifs-cta" onClick={() => scrollToDemo(OBJETS[1], pack.nom)}>
                {pack.parAgent ? 'Demander un devis' : 'Nous contacter'}
              </button>
            </div>
          ))}
        </div>
        {/* ⚠️ CETTE NOTE AFFIRMAIT L'INVERSE DE CE QUE LE CODE FACTURE. Elle disait
            « votre facture ne change pas avec les fluctuations d'effectif » alors que
            `calculerFacture()` applique max(effectif_actif × tarif + modules − remise,
            plancher), sur un effectif recompté à CHAQUE génération
            (`statut = 'actif' AND supprime_le IS NULL`). La facture bouge donc à
            chaque agent, sauf sous le plancher — et les trois cartes disaient déjà
            « vous ne payez que vos agents actifs », ce qui était vrai. Deux phrases
            contradictoires sur la même page, l'une des deux fausse.
            ⓘ CE QUI EST FIXE, C'EST LE TARIF UNITAIRE, PAS LE MONTANT. C'est la
              distinction que l'ancienne note écrasait : un reste du forfait fixe
              par tranche, abandonné le 03/09/2026 au profit du prix par agent. */}
        {/* ⚠️ UN SEUL BLOC, TROIS LIGNES (14/09/2026, décision de Dom).
            Il y en avait DEUX, voisins et de même classe : le lot du 13/09 avait
            AJOUTÉ un second bloc de note au lieu d'écrire dans le premier.
            ⓘ Le nom de la classe n'est pas cité ici : un commentaire qui recopie
              le markup remonte en faux positif à chaque grep sur le code vivant,
              et fait compter deux occurrences là où il n'y en a qu'une.
            Deux blocs identiques côte à côte, c'est deux endroits où
            corriger la note le jour où elle change — et un seul qu'on pense à
            ouvrir. C'est d'ailleurs ce qui s'est produit : la mention
            « hébergement dédié » a survécu à trois lots touchant cette page.

            ⓘ Ligne 3 : phrase de Dom, mot pour mot (13/09/2026). Elle dit ce que
              le moteur fait RÉELLEMENT — dix mois d'avance, puis la DIFFÉRENCE
              quand l'effectif dépasse celui de la souscription. Elle remplace la
              promesse « 2 mois offerts », qui vivait à deux endroits (la FAQ et
              le badge de chaque carte) et annonçait deux mois gratuits sans dire
              que les compléments restent dus.

            ⚠️ MENTION « HÉBERGEMENT DÉDIÉ » RETIRÉE — et sa justification BORNE
              sa validité au lieu de la dater seulement (règle 59) :
              **vrai tant qu'aucun prospect ne pose la question — 14/09/2026.**
              Prémisse : vingt ans d'exploitation en gardiennage sans qu'un seul
              client demande où sont stockées les données. Structurel : la société
              de gardiennage détient SES propres données (agents, affectations,
              registres de poste), pas celles de son donneur d'ordre. La phrase
              répondait à une objection qui n'existe pas.
              ➡️ Le jour où un prospect la pose, cette prémisse tombe et la
                 décision se rouvre — elle ne se défend plus toute seule. */}
        <div className="vt-tarifs-footer-note">
          Sans engagement — facturation mensuelle ou annuelle.<br />
          Votre tarif par agent est fixe dans votre tranche ; le montant suit votre effectif actif du mois, avec le forfait minimum de votre pack comme plancher.<br />
          Payez dix mois d&apos;avance. Votre redevance couvre l&apos;effectif de souscription ; au-delà, vous ne réglez que la différence.
        </div>
      </section>

      {/* Formulaire démo */}
      <section id="demo" className="vt-demo">
        <h2 className="vt-demo-title">Une démo, un devis ? Parlons-en.</h2>
        <p className="vt-demo-sub">Un expert vous répond sous 24&nbsp;h.</p>

        {sent ? (
          <div className="vt-demo-success">
            <svg className="vt-demo-success-icon" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <h3>Merci, votre demande a bien été reçue.</h3>
            <p>Notre équipe vous contactera sous 24&nbsp;h.</p>
          </div>
        ) : (
          <form className="vt-demo-form" onSubmit={handleSubmit}>
            <div className="vt-demo-field">
              <label className="vt-demo-label">Objet de votre demande <span className="vt-req">*</span></label>
              <select className="vt-demo-input" value={form.objet} onChange={updateField('objet')} required>
                {OBJETS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            {/* ⛔ PLUS UN MENU : UNE DÉDUCTION. Voir `offreDeduite` plus haut.
                « Je ne sais pas encore » est le cas NORMAL — un prospect qui
                ignore son effectif ne doit pas être forcé de nommer une offre. */}
            <div className="vt-demo-field">
              <label className="vt-demo-label">Offre correspondante</label>
              <p className="vt-demo-deduite">
                {offreDeduite
                  ? `Votre effectif correspond à l'offre ${offreDeduite.code}.`
                  : "Je ne sais pas encore — précisez un effectif, ou laissez « Non précisé »."}
              </p>
            </div>
            <div className="vt-demo-field">
              <label className="vt-demo-label">Nom complet <span className="vt-req">*</span></label>
              <input className="vt-demo-input" type="text" required maxLength={200}
                value={form.nom} onChange={updateField('nom')} />
            </div>
            <div className="vt-demo-field">
              <label className="vt-demo-label">Entreprise <span className="vt-req">*</span></label>
              <input className="vt-demo-input" type="text" required maxLength={200}
                value={form.entreprise} onChange={updateField('entreprise')} />
            </div>
            <div className="vt-demo-field">
              <label className="vt-demo-label">Nombre d'agents approximatif</label>
              <select className="vt-demo-input" value={form.nombre_agents} onChange={updateField('nombre_agents')}>
                <option value="">— Non précisé —</option>
                {/* ⛔ GÉNÉRÉ depuis OFFRES : aucune tranche écrite ici. */}
                {OFFRES.map((o) => (
                  <option key={o.code} value={libelleTranche(o)}>{libelleTranche(o)}</option>
                ))}
              </select>
            </div>
            <div className="vt-demo-field">
              <label className="vt-demo-label">Email <span className="vt-req">*</span></label>
              <input className="vt-demo-input" type="email" required maxLength={200}
                value={form.email} onChange={updateField('email')} />
            </div>
            <div className="vt-demo-field">
              <label className="vt-demo-label">Téléphone</label>
              <input className="vt-demo-input" type="tel" maxLength={50}
                value={form.telephone} onChange={updateField('telephone')} />
            </div>
            <div className="vt-demo-field">
              <label className="vt-demo-label">Message <span style={{ color: '#999', fontWeight: 400 }}>(optionnel)</span></label>
              <textarea className="vt-demo-input vt-demo-textarea" maxLength={2000}
                value={form.message} onChange={updateField('message')} />
            </div>
            <button type="submit" className="vt-demo-submit" disabled={sending}>
              {sending ? 'Envoi en cours...' : 'Envoyer ma demande'}
            </button>
            {error && <p className="vt-demo-error">{error}</p>}
            <p className="vt-demo-rgpd">
              Vos données sont utilisées uniquement pour traiter votre demande et ne sont pas partagées avec des tiers.
            </p>
          </form>
        )}
      </section>

      {/* Footer */}
      <footer className="vt-footer">
        <div className="vt-footer-inner">
          <div>
            <div className="vt-footer-name-row">
              {/* ⚠️ LA MARQUE DE L'ÉDITEUR, À SA PLACE : au pied de page, à gauche de
                  sa qualité et de ses identifiants légaux.
                  ⚠️ PASTILLE CLAIRE, ET C'EST LE FICHIER QUI L'IMPOSE. Mesuré dans les
                     pixels le 11/09/2026 : `RGBA(253,253,253,255)` aux quatre coins —
                     fond OPAQUE quasi blanc, malgré un PNG de type 6 (RGBA). Posé nu
                     sur le #16161C du pied de page, il devient un rectangle lumineux.
                     L'envelopper dans un bloc clair rend la chose VOULUE au lieu
                     d'accidentelle. ➡️ À remplacer par un PNG détouré le jour où il
                     existera : la pastille est un traitement, pas une fin.
                  ⚠️ `src="/logo-gladiator.png"` pointe vers `frontend/public/`, PAS vers
                     un import webpack : `index.html` ne sait atteindre que `public/`
                     pour le favicon — et demain pour l'`og:image`, qui exige une URL
                     stable qu'un nom haché par webpack ne peut pas donner.
                  ⚠️ `width`/`height` intrinsèques déclarés (292×234) : sans eux le
                     navigateur réserve 0 et le pied de page saute au chargement.
                  ⓘ LA LIGNE « Gladiator Security Systems » A ÉTÉ RETIRÉE ICI : le logo
                    porte DÉJÀ son propre wordmark. La garder affichait la raison
                    sociale deux fois, à 12 px d'intervalle. Elle survit dans l'`alt`,
                    qui est le seul endroit où elle manquerait à un lecteur d'écran. */}
              <span className="vt-footer-pastille">
                <img
                  src="/logo-gladiator.png"
                  alt="Gladiator Security Systems, éditeur de GSS-360"
                  className="vt-footer-logo"
                  width="292"
                  height="234"
                  loading="lazy"
                />
              </span>
              <div>
                <div className="vt-footer-tagline">Éditeur de GSS-360, intégrateur de systèmes de sécurité</div>
              </div>
            </div>
            <div className="vt-footer-legal">
              RCCM TG-LFW-01-2024-B13-00449 · NIF 1001930788<br />
              Tokoin-Wuiti, Rue Air-Burkina, Lomé
            </div>
          </div>
          <div>
            <div className="vt-footer-col-title">Contact</div>
            <div className="vt-footer-contact">
              +228 92 53 15 14<br />+228 22 61 09 61
            </div>
            <a href="https://wa.me/22890054083" target="_blank" rel="noopener noreferrer" className="vt-footer-wa">
              <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp : +228 90 05 40 83
            </a>
          </div>
          <div>
            <div className="vt-footer-col-title">Liens</div>
            <div className="vt-footer-links">
              <a href="/mentions-legales" className="vt-footer-link">Mentions légales</a>
              <a href="/confidentialite" className="vt-footer-link">Confidentialité</a>
              <a href="/faq" className="vt-footer-link">FAQ</a>
              <a href="/login" className="vt-footer-link">Se connecter</a>
            </div>
          </div>
        </div>
        <div className="vt-footer-copy">
          © 2026 Gladiator Security Systems. Tous droits réservés.
        </div>
      </footer>

      {/* WhatsApp flottant */}
      <a
        href="https://wa.me/22890054083?text=Bonjour%2C%20je%20souhaite%20en%20savoir%20plus%20sur%20GSS-360."
        target="_blank"
        rel="noopener noreferrer"
        className="vt-whatsapp"
        title="Nous écrire sur WhatsApp"
      >
        <svg viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
